// Wizard Step 4 — Хяналт + Илгээх (screens-buyer-app §7).
//
// Read-only summary cards + computed refund preview + consent checkbox. The
// actual submit (createReturn) is owned by ReturnWizard; this step only renders
// the review and reports consent up. Target warehouse (D12) is resolved
// SERVER-SIDE at create-time, so it is shown informationally only.
import { HelpIcon } from "@dehub/theme";
import { Box, Checkbox, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { IRefundTimelinePreview, RefundMethod, ReturnReason, ShippingResponsibility } from "../../../../apis/return";
import { currencyFormat } from "../../../../utils";
import {
  refundEtaLabel,
  refundMethodLabelMn,
  reasonLabelMn,
  shippingResponsibilityLabelMn,
  TNUM,
} from "../constants";
import { subReasonLabelMn } from "./sub-reasons";
import type { WizardSelectedItem, WizardState } from "./state";
import { selectedItems, selectedTotal } from "./state";
import classes from "./wizard-stepper.module.css";

interface Props {
  order: { orderNumber: string; deliveredAt: string | null };
  state: WizardState;
  reason: ReturnReason | null;
  /** Seller (counterparty) name — backs the D12 target-warehouse default label. */
  sellerName?: string | null;
  /** B67: BE refund-timeline previews keyed by refund method. */
  refundEta?: Partial<Record<string, IRefundTimelinePreview>>;
  onConsent: (value: boolean) => void;
}

/** Canvas card: 14/600 grey label, 16/650 value, radius 14. */
function SummaryCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box className={classes.summaryCard}>
      <Text className={classes.summaryLabel}>{title}</Text>
      {children}
    </Box>
  );
}

export function StepReview({ order, state, reason, sellerName, refundEta, onConsent }: Props) {
  const { t } = useTranslation("buyerApp");
  const items = selectedItems(state);
  const total = selectedTotal(state);
  const refundMethod = state.preferredRefundMethod ? (state.preferredRefundMethod as RefundMethod) : null;
  const shippingLabel = state.shippingResponsibility
    ? shippingResponsibilityLabelMn(state.shippingResponsibility as ShippingResponsibility)
    : "-";

  return (
    <Stack gap={14}>
      <Box>
        <Text className={classes.title}>{t("stepReview.title")}<HelpIcon helpKey="order.buyer-app-my-returns-new.section.review" variant="section" /></Text>
        <Text className={classes.subtitle}>{t("stepReview.subtitle")}</Text>
      </Box>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
        <SummaryCard title={t("stepReview.order")}>
          <Text className={classes.summaryValue} ff="monospace" translate="no">
            {order.orderNumber}
          </Text>
          {order.deliveredAt ? (
            <Text size="sm" c="dimmed">
              {t("stepReview.deliveredOn", { date: order.deliveredAt.slice(0, 10) })}
            </Text>
          ) : null}
        </SummaryCard>

        <SummaryCard title={t("stepReview.items", { count: items.length })}>
          <Stack gap={4}>
            {items.map((it: WizardSelectedItem) => (
              <Group key={it.orderItemId} justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm" lineClamp={1}>
                  {t("stepReview.itemLine", { name: it.productName, quantity: it.quantity })}
                </Text>
                <Text size="sm" fw={500} style={{ ...TNUM, whiteSpace: "nowrap" }}>
                  {currencyFormat(it.unitPrice * it.quantity)}
                </Text>
              </Group>
            ))}
          </Stack>
        </SummaryCard>
      </SimpleGrid>

      <SummaryCard title={t("stepReview.reason")}>
        <Text className={classes.summaryValue}>{reason ? reasonLabelMn(reason) : "-"}</Text>
        {state.subReason ? (
          <Text size="sm" c="dimmed">
            {t("stepReview.subReasonPrefix", { value: subReasonLabelMn(reason, state.subReason) })}
          </Text>
        ) : null}
        {state.reasonDetails.trim() ? (
          <Text size="sm" mt={4}>
            &ldquo;{state.reasonDetails.trim()}&rdquo;
          </Text>
        ) : null}
      </SummaryCard>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
        <SummaryCard title={t("stepReview.photos")}>
          <Text>{t("stepReview.photoCount", { count: state.evidencePhotos.length })}</Text>
        </SummaryCard>

        <SummaryCard title={t("stepReview.refundChannel")}>
          {refundMethod ? (
            <Box>
              <Text className={classes.summaryValue}>{refundMethodLabelMn(refundMethod)}</Text>
              <Text size="sm" c="dimmed">
                {refundEtaLabel(refundEta?.[refundMethod])}
              </Text>
            </Box>
          ) : (
            <Text c="dimmed">{t("stepReview.refundNotChosen")}</Text>
          )}
        </SummaryCard>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
        <SummaryCard title={t("stepReview.shipping")}>
          <Text className={classes.summaryValue}>{shippingLabel}</Text>
          <Text size="sm" c="dimmed">
            {t("stepReview.shippingRequestNote")}
          </Text>
        </SummaryCard>

        <SummaryCard title={t("stepReview.receivingWarehouse")}>
          <Text className={classes.summaryValue}>
            {sellerName ? t("stepReview.warehouseNamed", { name: sellerName }) : t("stepReview.warehouseAuto")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("stepReview.warehouseAutoDesc")}
          </Text>
        </SummaryCard>
      </SimpleGrid>

      {/* Computed refund preview (canvas: grey ground, centred mono figure). */}
      <Box className={classes.preview}>
        <Text className={classes.summaryLabel}>{t("stepReview.preview")}</Text>
        <Text className={classes.previewAmount}>{currencyFormat(total)}</Text>
        <Text className={classes.subtitle} ta="center">
          {t("stepReview.previewNote")}
        </Text>
      </Box>

      <Box className={classes.summaryCard}>
        <Checkbox
          color="dark"
          size="md"
          checked={state.consent}
          onChange={(e) => onConsent(e.currentTarget.checked)}
          label={t("stepReview.consent")}
        />
      </Box>
    </Stack>
  );
}
